import { Picture } from "@/components/shared/Picture/Picture";
import { RichTextImageQueryResult } from "@/sanity/queries";
import { PortableText, PortableTextProps, PortableTextReactComponents } from "next-sanity";
import { RichTextDownload } from "../../rich-text/RichTextDownload/RichTextDownload";
import { Highlight } from "../Highlight/Highlight";
import SiteLink from "../SiteLink/SiteLink";
import styles from "./PortableTextRichText.module.scss";

interface PortableTextRichTextProps {
	value: PortableTextProps["value"];
	asFragment?: boolean;
}

export const PortableTextRichText = ({ value, asFragment }: PortableTextRichTextProps) => {
	const components = { ...portableTextComponents };

	if (asFragment) {
		components.block = ({ children }: { children?: React.ReactNode }) => <>{children}</>;
	}

	return (
		<PortableText
			value={value}
			components={components}
		/>
	);
};

export const portableTextComponents: Partial<PortableTextReactComponents> = {
	marks: {
		link: ({ children, value }) => {
			const href = value?.link;
			return (
				<SiteLink
					href={href ?? "#"}
					className={styles["link"]}
					target={value?.openInNewTab ? "_blank" : undefined}
				>
					{children}
				</SiteLink>
			);
		},
		highlight: ({ children }) => <Highlight>{children}</Highlight>,
	},
	types: {
		download: ({ value }) => (
			<RichTextDownload
				file={value.download}
				label={value.title}
			/>
		),
		image: ({ value }) => {
			const image = value as RichTextImageQueryResult;
			return (
				<p>
					<Picture
						sanityImage={image}
						alt={image.altText}
						pixelDensities={[1]}
						width={image.width}
						height={image.height}
					/>
				</p>
			);
		},
	},

	list: {
		bullet: ({ children }) => <ul className={styles["ul"]}>{children}</ul>,
		number: ({ children }) => <ol className={styles["ol"]}>{children}</ol>,
	},
	unknownBlockStyle: v => {
		console.error("PortableText Error: Unknown block style", v);
		return null;
	},
	unknownMark: v => {
		console.error("PortableText Error: Unknown mark", v);
		return null;
	},
	unknownList: v => {
		console.error("PortableText Error: Unknown list", v);
		return null;
	},
	unknownListItem: v => {
		console.error("PortableText Error: Unknown list item", v);
		return null;
	},
	unknownType: v => {
		console.error("PortableText Error: Unknown type", v);
		return null;
	},
};
